import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger
} from '@radix-ui/react-accordion';
import { format } from 'date-fns';
import {
  ChevronDownIcon,
  DownloadIcon,
  EyeIcon,
  FilesIcon,
  InfoIcon,
  Loader2Icon,
  Trash2Icon,
  UploadIcon,
  Wand2,
  XIcon
} from 'lucide-react';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { toast } from 'sonner';

import useOutletContext from '../useOutletContext';

import ActivityTab from './ActivityTab';
import CommentsTab from './CommentsTab';
import { ParseCimModal, type ParsedCimData } from './ParseCimModal';
import { TeamMemberSelector } from './TeamMemberSelector';

import { PdfViewerDialog } from '@/components/reports/PdfViewerDialog';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { capitalize, fileToBase64 } from '@/lib/utils';

interface UpdateDealOpportunityTransactionData {
  updateOpportunity: {
    ok: boolean;
  };
}

interface UpdateDealOpportunityTransactionVariables {
  closing_date?: null | string;
  deal_opportunity_id: string;
  emailBody?: string;
  equityThesis?: string;
  footnotes?: string;
  investment_thesis?: string;
  junior_team_ids?: string[];
  md_team_id?: null | string;
  md2_team_id?: null | string;
  mid_team_ids?: string[];
  transaction_background?: string;
  transaction_description?: string;
}

const UPDATE_DEAL_OPPORTUNITY_TRANSACTION = gql`
  mutation UpdateDealOpportunityTransaction(
    $deal_opportunity_id: ID!
    $investment_thesis: String
    $transaction_background: String
    $transaction_description: String
    $footnotes: String
    $md_team_id: ID
    $md2_team_id: ID
    $mid_team_ids: [ID]
    $junior_team_ids: [ID]
    $equityThesis: String
    $closing_date: Date
    $emailBody: String
  ) {
    updateOpportunity(
      id: $deal_opportunity_id
      values: {
        investmentThesis: $investment_thesis
        transactionBackground: $transaction_background
        transactionDescription: $transaction_description
        footnotes: $footnotes
        mdTeamId: $md_team_id
        md2TeamId: $md2_team_id
        midTeamIds: $mid_team_ids
        juniorTeamIds: $junior_team_ids
        equityThesis: $equityThesis
        closingDate: $closing_date
        emailBody: $emailBody
      }
    ) {
      ok
    }
  }
`;

interface DealOpportunityData {
  node: {
    cim: null | string;
    cimFilename: null | string;
    cimMetadata: null | ParsedCimData;
    claudeCimErrorMessage: null | string;
    claudeCimStatus: null | string;
    closingDate: null | string;
    createDate: string;
    dueDiligenceProjectId: {
      nextMilestoneName: null | string;
      rootTasks: {
        childIds:
          | {
              state: null | string;
            }[]
          | null;
      }[];
    } | null;
    emailBody: null | string;
    id: string;
    juniorTeamIds: { id: string; name: string }[];
    killedDealNotes: null | string;
    killReason: null | string;
    md2TeamId: { id: string; name: string } | null;
    mdTeamId: { id: string; name: string } | null;
    midTeamIds: { id: string; name: string }[];
    name: string;
    nda: null | string;
    ndaFilename: null | string;
    pipeline: null | string;
    projectName: string;
    smcSource: { name: string } | null;
    stage: null | string;
    status: null | string;
    targetEntityId: {
      __typename: 'AccountNode';
      id: string;
      name: string;
    } | null;
    targetEntityName: null | string;
    teaser: null | string;
    teaserFilename: null | string;
  } | null;
}

interface UpdateDealOpportunityStatusData {
  updateOpportunity: {
    ok: boolean;
  };
}

interface UpdateDealOpportunityStatusVariables {
  deal_opportunity_id: string;
  status?: string;
}

const DEAL_OPPORTUNITY_QUERY = gql`
  query DealOpportunity($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        id
        name
        stage
        status
        closingDate
        pipeline
        targetEntityName
        mdTeamId {
          id
          name
        }
        md2TeamId {
          id
          name
        }
        midTeamIds {
          id
          name
        }
        juniorTeamIds {
          id
          name
        }
        dueDiligenceProjectId {
          nextMilestoneName
          nextMilestoneId {
            name
            displayName
          }
          rootTasks {
            childIds {
              state
            }
          }
        }
        cim
        cimFilename
        cimMetadata
        claudeCimStatus
        claudeCimErrorMessage
        teaser
        teaserFilename
        nda
        ndaFilename
        emailBody
      }
    }
  }
`;

const DEAL_OPPORTUNITY_CIM_STATUS_QUERY = gql`
  query DealOpportunityCimStatus($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        id
        claudeCimStatus
        claudeCimErrorMessage
        cimMetadata
      }
    }
  }
`;

interface DealOpportunityCimStatusData {
  node: {
    cimMetadata: null | ParsedCimData;
    claudeCimErrorMessage: null | string;
    claudeCimStatus: null | string;
    id: string;
  } | null;
}

const UPDATE_DEAL_OPPORTUNITY_STATUS = gql`
  mutation UpdateDealOpportunityStatus(
    $deal_opportunity_id: ID!
    $status: String
  ) {
    updateOpportunity(id: $deal_opportunity_id, values: { status: $status }) {
      ok
    }
  }
`;

interface UploadCimData {
  uploadCim: {
    ok: boolean;
  };
}

interface UploadCimVariables {
  cim: string;
  id: string;
}

interface UploadNdaData {
  uploadNda: {
    ok: boolean;
  };
}

interface UploadNdaVariables {
  id: string;
  nda: string;
}

interface UploadTeaserData {
  uploadTeaser: {
    ok: boolean;
  };
}

interface UploadTeaserVariables {
  id: string;
  teaser: string;
}

const UPLOAD_CIM = gql`
  mutation UploadCim($id: ID!, $cim: String!) {
    uploadCim(opportunityId: $id, fileContent: $cim) {
      ok
    }
  }
`;

const UPLOAD_NDA = gql`
  mutation UploadNda($id: ID!, $nda: String!) {
    uploadNda(opportunityId: $id, fileContent: $nda) {
      ok
    }
  }
`;

const UPLOAD_TEASER = gql`
  mutation UploadTeaser($id: ID!, $teaser: String!) {
    uploadTeaser(opportunityId: $id, fileContent: $teaser) {
      ok
    }
  }
`;

const PARSE_CIM_WITH_CLAUDE = gql`
  mutation ParseCimWithClaude($id: ID!) {
    parseCimWithClaude(opportunityId: $id) {
      ok
      message
      data
    }
  }
`;

interface ParseCimWithClaudeData {
  parseCimWithClaude: {
    data: null | ParsedCimData;
    message: string;
    ok: boolean;
  };
}

interface ParseCimWithClaudeVariables {
  id: string;
}

const UPDATE_OPPORTUNITY_FROM_CIM_DATA = gql`
  mutation UpdateOpportunityFromCimData(
    $opportunityId: ID!
    $data: GenericScalar!
  ) {
    updateOpportunityFromCimData(opportunityId: $opportunityId, data: $data) {
      ok
      message
      opportunity {
        id
        name
        projectName
        revenuePercentageUs
        headquarters
        yearFounded
        investmentThesis
      }
    }
  }
`;

interface UpdateOpportunityFromCimDataData {
  updateOpportunityFromCimData: {
    message: string;
    ok: boolean;
    opportunity: {
      headquarters: null | string;
      id: string;
      investmentThesis: null | string;
      name: string;
      projectName: string;
      revenuePercentageUs: null | number;
      yearFounded: null | number;
    };
  };
}

interface UpdateOpportunityFromCimDataVariables {
  data: ParsedCimData;
  opportunityId: string;
}

const STATUS_OPTIONS = [
  { name: 'Not Enough Information', value: 'not_enough_information' },
  { name: 'Ready for MD Review', value: 'ready_for_md_review' },
  { name: 'Active', value: 'active' },
  { name: 'Terminated Long Term', value: 'long_term' },
  { name: 'Terminated Killed', value: 'killed' },
  { name: 'Closed', value: 'closed' }
] as const;

interface Props {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const RightSidebar = ({ setIsOpen }: Props) => {
  const { id } = useOutletContext();

  const {
    data,
    loading,
    refetch: refetchDeal
  } = useQuery<DealOpportunityData>(DEAL_OPPORTUNITY_QUERY, {
    skip: !id,
    variables: { id }
  });
  const [isPollingCimStatus, setIsPollingCimStatus] = useState(false);
  const parseCimRequestedRef = useRef(false);

  const { data: cimStatusData } = useQuery<DealOpportunityCimStatusData>(
    DEAL_OPPORTUNITY_CIM_STATUS_QUERY,
    {
      fetchPolicy: 'network-only',
      pollInterval: isPollingCimStatus ? 5000 : 0,
      skip: !id,
      variables: { id }
    }
  );

  const opportunity = data?.node;
  const cimStatusNode = cimStatusData?.node;

  useEffect(() => {
    const status = cimStatusNode?.claudeCimStatus;
    if (status === 'pending' || status === 'processing') {
      setIsPollingCimStatus(true);
    }
    if (status === 'completed') {
      setIsPollingCimStatus(false);
    }
  }, [cimStatusNode?.claudeCimStatus]);

  const projectName = opportunity?.name ?? '';
  const companyName = opportunity?.targetEntityName ?? '-';
  const closingDate = opportunity?.closingDate ?? null;
  const status = opportunity?.status ?? null;
  const stage = opportunity?.stage ?? null;
  const phase = opportunity?.dueDiligenceProjectId?.nextMilestoneName ?? null;
  const pipeline = opportunity?.pipeline ?? null;
  const dealLead = opportunity?.mdTeamId;
  const supportingLead = opportunity?.md2TeamId;
  const midLevelTeam = opportunity?.midTeamIds ?? [];
  const juniorTeam = opportunity?.juniorTeamIds ?? [];

  const isClosedHidden = useMemo(() => {
    const rootTasks = opportunity?.dueDiligenceProjectId?.rootTasks;
    if (!rootTasks || rootTasks.length === 0) return true;
    const lastRootTask = rootTasks[rootTasks.length - 1];
    const childIds = lastRootTask.childIds;
    if (!childIds || childIds.length === 0) return true;
    const lastChild = childIds[childIds.length - 1];
    return lastChild.state !== '1_done';
  }, [opportunity]);

  const availableStatuses = useMemo(() => {
    const currentStatusOption =
      STATUS_OPTIONS.find(option => option.value === status) ??
      STATUS_OPTIONS.find(option => option.value === 'active');
    const filteredOptions = STATUS_OPTIONS.filter(option => {
      switch (option.value) {
        case 'active':
          return stage !== 'active';
        case 'closed':
          return !isClosedHidden;
        case 'killed':
          return status !== 'killed';
        case 'long_term':
          return status !== 'long_term';
        case 'not_enough_information':
          return !(
            status === 'not_enough_information' || stage !== 'top_of_funnel'
          );
        case 'ready_for_md_review':
          return !(
            status === 'ready_for_md_review' || stage !== 'top_of_funnel'
          );

        default:
          return true;
      }
    });

    if (
      currentStatusOption &&
      !filteredOptions.some(opt => opt.value === currentStatusOption.value)
    ) {
      return [currentStatusOption, ...filteredOptions];
    }

    return filteredOptions;
  }, [status, stage, isClosedHidden]);

  const syndicationBadge = useMemo(() => {
    if (!phase) {
      return { text: 'N/A', variant: 'pastel-neutral' as const };
    }

    const phaseLower = phase.toLowerCase();

    if (phaseLower === 'icb' || phaseLower === 'term sheet') {
      return { text: 'Early stage', variant: 'pastel-blue' as const };
    }

    if (
      phaseLower === 'icm' ||
      phaseLower === 'ficm' ||
      phaseLower === 'legal, tax, esg' ||
      phaseLower === 'closing'
    ) {
      return { text: 'Active', variant: 'pastel-purple' as const };
    }

    return { text: 'N/A', variant: 'pastel-neutral' as const };
  }, [phase]);

  const files = useMemo(() => {
    if (!opportunity) return [];

    const fileList: {
      fileData: null | string;
      id: string;
      name: string;
      type: 'cim' | 'nda' | 'teaser';
      uploadDate: string;
    }[] = [];

    if (opportunity.cim) {
      fileList.push({
        fileData: opportunity.cim,
        id: 'cim',
        name:
          opportunity.cimFilename ??
          `${projectName}-CIM-${format(new Date(), 'MM-dd-yyyy')}.pdf`,
        type: 'cim',
        uploadDate: 'CIM Document'
      });
    }

    if (opportunity.nda) {
      fileList.push({
        fileData: opportunity.nda,
        id: 'nda',
        name:
          opportunity.ndaFilename ??
          `${projectName}-NDA-${format(new Date(), 'MM-dd-yyyy')}.pdf`,
        type: 'nda',
        uploadDate: 'NDA Document'
      });
    }

    if (opportunity.teaser) {
      fileList.push({
        fileData: opportunity.teaser,
        id: 'teaser',
        name:
          opportunity.teaserFilename ??
          `${projectName}-Teaser-${format(new Date(), 'MM-dd-yyyy')}.pdf`,
        type: 'teaser',
        uploadDate: 'Teaser Document'
      });
    }

    return fileList;
  }, [opportunity, projectName]);

  const [updateDealOpportunity] = useMutation<
    UpdateDealOpportunityTransactionData,
    UpdateDealOpportunityTransactionVariables
  >(UPDATE_DEAL_OPPORTUNITY_TRANSACTION, {
    refetchQueries: id
      ? [{ query: DEAL_OPPORTUNITY_QUERY, variables: { id } }]
      : []
  });

  const [updateDealOpportunityStatus] = useMutation<
    UpdateDealOpportunityStatusData,
    UpdateDealOpportunityStatusVariables
  >(UPDATE_DEAL_OPPORTUNITY_STATUS, {
    refetchQueries: id
      ? [{ query: DEAL_OPPORTUNITY_QUERY, variables: { id } }]
      : []
  });

  const [uploadCim] = useMutation<UploadCimData, UploadCimVariables>(
    UPLOAD_CIM,
    {
      refetchQueries: [{ query: DEAL_OPPORTUNITY_QUERY, variables: { id } }]
    }
  );

  const [uploadNda] = useMutation<UploadNdaData, UploadNdaVariables>(
    UPLOAD_NDA,
    {
      refetchQueries: [{ query: DEAL_OPPORTUNITY_QUERY, variables: { id } }]
    }
  );

  const [uploadTeaser] = useMutation<UploadTeaserData, UploadTeaserVariables>(
    UPLOAD_TEASER,
    {
      refetchQueries: [{ query: DEAL_OPPORTUNITY_QUERY, variables: { id } }]
    }
  );

  const [parseCimWithClaude, { loading: parseCimLoading }] = useMutation<
    ParseCimWithClaudeData,
    ParseCimWithClaudeVariables
  >(PARSE_CIM_WITH_CLAUDE);

  const [updateOpportunityFromCimData] = useMutation<
    UpdateOpportunityFromCimDataData,
    UpdateOpportunityFromCimDataVariables
  >(UPDATE_OPPORTUNITY_FROM_CIM_DATA, {
    refetchQueries: id
      ? [{ query: DEAL_OPPORTUNITY_QUERY, variables: { id } }]
      : []
  });

  const [localStatus, setLocalStatus] = useState<string>(status ?? 'active');
  const [parseCimModalOpen, setParseCimModalOpen] = useState(false);
  const [parsedCimData, setParsedCimData] = useState<null | ParsedCimData>(
    null
  );
  const [cimPopoverOpen, setCimPopoverOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() =>
    closingDate ? new Date(closingDate) : undefined
  );
  const [isUploadSheetOpen, setIsUploadSheetOpen] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState<string | undefined>(
    undefined
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileFieldErrorMessage, setFileFieldErrorMessage] = useState<
    null | string
  >(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerFileData, setViewerFileData] = useState<null | string>(null);
  const [viewerFilename, setViewerFilename] = useState<string>('');
  const [localEmailBody, setLocalEmailBody] = useState<string>('');
  const emailBodyDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLocalStatus(status ?? 'active');
  }, [status]);

  useEffect(() => {
    setSelectedDate(closingDate ? new Date(closingDate) : undefined);
  }, [closingDate]);

  useEffect(() => {
    setLocalEmailBody((opportunity?.emailBody as string | undefined) ?? '');
  }, [opportunity?.emailBody]);

  useEffect(() => {
    if (emailBodyDebounceTimeoutRef.current) {
      clearTimeout(emailBodyDebounceTimeoutRef.current);
    }

    emailBodyDebounceTimeoutRef.current = setTimeout(() => {
      void (async () => {
        try {
          await updateDealOpportunity({
            variables: {
              deal_opportunity_id: id,
              emailBody: localEmailBody
            }
          });
        } catch (error) {
          console.error('Failed to update Email Body:', error);
          toast.error('Failed to update Email Body');
        }
      })();
    }, 1000);

    return () => {
      if (emailBodyDebounceTimeoutRef.current) {
        clearTimeout(emailBodyDebounceTimeoutRef.current);
      }
    };
  }, [localEmailBody, id, updateDealOpportunity]);

  useEffect(() => {
    if (!cimStatusNode) return;
    const status = cimStatusNode.claudeCimStatus;
    if (status === 'completed' && parseCimRequestedRef.current) {
      setIsPollingCimStatus(false);
      const raw = cimStatusNode.cimMetadata;
      if (raw) {
        const parsed =
          typeof raw === 'string' ? (JSON.parse(raw) as ParsedCimData) : raw;
        setParsedCimData(parsed);
        setParseCimModalOpen(true);
        toast.success('CIM parsed successfully');
      }
      parseCimRequestedRef.current = false;
      void refetchDeal();
    } else if (status === 'error') {
      setIsPollingCimStatus(false);
      if (parseCimRequestedRef.current) {
        toast.error(
          cimStatusNode.claudeCimErrorMessage ??
            'Failed to parse CIM with Claude'
        );
      }
      parseCimRequestedRef.current = false;
    }
  }, [cimStatusNode, refetchDeal]);

  const hasCimMetadata = Boolean(
    opportunity?.cimMetadata &&
      (typeof opportunity.cimMetadata !== 'object' ||
        Object.keys(opportunity.cimMetadata as Record<string, unknown>).length >
          0)
  );

  const handleTriggerParseCim = useCallback(async () => {
    if (!id) return;
    try {
      setParsedCimData(null);
      const result = await parseCimWithClaude({ variables: { id } });
      const payload = result.data?.parseCimWithClaude;
      if (!payload?.ok) {
        toast.error(payload?.message ?? 'Failed to parse CIM');
        return;
      }
      toast.success(
        payload.message || 'CIM parsing started, results will appear shortly.'
      );
      parseCimRequestedRef.current = true;
      setIsPollingCimStatus(true);
    } catch (error) {
      console.error('Error parsing CIM:', error);
      toast.error('Failed to parse CIM');
    }
  }, [id, parseCimWithClaude]);

  const handleViewParsedCimData = useCallback(() => {
    const raw = opportunity?.cimMetadata;
    if (!raw) return;
    const data: ParsedCimData =
      typeof raw === 'string' ? (JSON.parse(raw) as ParsedCimData) : raw;
    setParsedCimData(data);
    setParseCimModalOpen(true);
    setCimPopoverOpen(false);
  }, [opportunity?.cimMetadata]);

  const handleXButtonClick = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const downloadBase64File = useCallback(
    (base64String: string, filename: string) => {
      const match = /^data:([^;]+);base64,(.*)$/.exec(base64String.trim());

      const base64 = (match?.[2] ?? base64String).replace(/\s/g, '');

      const byteCharacters = atob(base64);
      const byteNumbers = new Array<number>(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: 'application/pdf;base64'
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    []
  );

  const handleViewFile = useCallback((fileData: string, filename: string) => {
    try {
      setViewerFileData(fileData);
      setViewerFilename(filename);
      setIsViewerOpen(true);
    } catch (error) {
      console.error('Error viewing file:', error);
      toast.error('Failed to open file');
    }
  }, []);

  const handleDownloadFile = useCallback(
    (fileData: string, filename: string) => {
      try {
        downloadBase64File(fileData, filename);
        toast.success('File downloaded successfully');
      } catch (error) {
        console.error('Error downloading file:', error);
        toast.error('Failed to download file');
      }
    },
    [downloadBase64File]
  );

  const handleDeleteFile = useCallback(
    async (fileType: 'cim' | 'nda' | 'teaser') => {
      try {
        if (fileType === 'cim') {
          const { error } = await uploadCim({
            variables: {
              cim: '',
              id
            }
          });

          if (error) {
            throw new Error('Failed to delete CIM file');
          }
        } else if (fileType === 'nda') {
          const { error } = await uploadNda({
            variables: {
              id,
              nda: ''
            }
          });

          if (error) {
            throw new Error('Failed to delete NDA file');
          }
        } else {
          const { error } = await uploadTeaser({
            variables: {
              id,
              teaser: ''
            }
          });

          if (error) {
            throw new Error('Failed to delete Teaser file');
          }
        }

        toast.success('File deleted successfully');
      } catch (error_) {
        const error = error_ as Error;
        console.error('Error deleting file:', error);
        toast.error(error.message || 'Failed to delete file');
      }
    },
    [id, uploadCim, uploadNda, uploadTeaser]
  );

  return (
    <>
      <div className="@container min-w-0 w-full space-y-5 overflow-x-hidden px-2 py-5 sm:px-2.5">
        <div className="flex min-w-0 justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate text-lg font-semibold">{projectName}</div>
            <div className="text-muted-foreground truncate text-sm font-medium">
              {companyName || '-'}
            </div>
          </div>
          <Button
            onClick={handleXButtonClick}
            size="icon-sm"
            variant="ghost"
          >
            <XIcon />
          </Button>
        </div>
        <Accordion
          collapsible
          defaultValue="more-info"
          type="single"
        >
          <AccordionItem
            className="rounded-md border p-2.5"
            value="more-info"
          >
            <AccordionHeader>
              <AccordionTrigger className="group space-between text-muted-foreground flex w-full items-center justify-between gap-x-5">
                <div className="text-sm font-medium">More Info</div>
                <ChevronDownIcon
                  aria-hidden
                  className="transition-transform group-data-[state=open]:-rotate-z-180"
                  size={16}
                />
              </AccordionTrigger>
            </AccordionHeader>
            <AccordionContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down mt-5 space-y-2.5 overflow-hidden">
              <div className="grid grid-cols-1 items-center gap-x-1 @[360px]:grid-cols-2">
                <div className="text-muted-foreground min-w-0 text-sm font-medium">
                  Closing Date
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      className="min-w-0 w-full justify-start text-left font-normal"
                      variant="outline"
                    >
                      {selectedDate ? (
                        format(selectedDate, 'MM/dd/yyyy')
                      ) : (
                        <span className="text-muted-foreground">
                          Select date
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="w-auto p-0"
                  >
                    <Calendar
                      defaultMonth={selectedDate}
                      mode="single"
                      onSelect={date => {
                        setSelectedDate(date);
                        void (async () => {
                          try {
                            await updateDealOpportunity({
                              variables: {
                                closing_date: date
                                  ? format(date, 'yyyy-MM-dd')
                                  : null,
                                deal_opportunity_id: id
                              }
                            });
                          } catch (error) {
                            console.error(
                              'Failed to update Closing Date:',
                              error
                            );
                            toast.error('Failed to update Closing Date');
                            setSelectedDate(
                              closingDate ? new Date(closingDate) : undefined
                            );
                          }
                        })();
                      }}
                      selected={selectedDate}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid grid-cols-1 items-center gap-x-1 @[360px]:grid-cols-2">
                <div className="text-muted-foreground min-w-0 text-sm font-medium">
                  Status
                </div>
                <div className="min-w-0">
                <Select
                  onValueChange={value => {
                    setLocalStatus(value);
                    void (async () => {
                      try {
                        await updateDealOpportunityStatus({
                          variables: {
                            deal_opportunity_id: id,
                            status: value
                          }
                        });
                      } catch (error) {
                        console.error('Failed to update Status:', error);
                        toast.error('Failed to update Status');
                        setLocalStatus(status ?? 'active');
                      }
                    })();
                  }}
                  value={localStatus}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={loading ? 'Loading...' : '-'} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableStatuses.map(option => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 items-center gap-x-1 @[360px]:grid-cols-2">
                <div className="text-muted-foreground flex min-w-0 items-center justify-between text-sm font-medium">
                  Phase
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                      >
                        <InfoIcon />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-64">
                      <p>
                        Phases are defined by task completion in the due
                        diligence checklist.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Badge className="min-w-0" variant="pastel-dark-blue">{phase ?? '-'}</Badge>
              </div>
              <div className="grid grid-cols-1 items-center gap-x-1 @[360px]:grid-cols-2">
                <div className="text-muted-foreground flex min-w-0 items-center justify-between text-sm font-medium">
                  Pipeline
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                      >
                        <InfoIcon />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-64">
                      <p>
                        Pipeline (Unmandated/Mandated) is defined automatically.
                      </p>
                      <ul className="ml-5 list-disc">
                        <li>
                          A deal is marked "Unmandated" when it enters Refined
                          Screening and exits the pipeline when a term-sheet is
                          signed.
                        </li>
                        <li>
                          A deal is marked "Mandated" when a term-sheet is
                          signed and exits the pipeline when the deal is
                          terminated.
                        </li>
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Badge
                  className="min-w-0"
                  variant={
                    pipeline?.toLowerCase() === 'mandated'
                      ? 'pastel-red'
                      : pipeline?.toLowerCase() === 'unmandated'
                        ? 'pastel-blue'
                        : 'pastel-neutral'
                  }
                >
                  {pipeline ? (capitalize(pipeline) ?? '-') : '-'}
                </Badge>
              </div>
              <div className="grid grid-cols-1 items-center gap-x-1 @[360px]:grid-cols-2">
                <div className="text-muted-foreground flex min-w-0 items-center justify-between text-sm font-medium">
                  Syndication
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                      >
                        <InfoIcon />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-64">
                      <p>
                        Syndication (Early Stage/Active) is defined
                        automatically.
                      </p>
                      <ul className="ml-5 list-disc">
                        <li>
                          A deal is marked "Early Stage" when it enters after
                          IOI is done and exits the pipeline when a term-sheet
                          is signed.
                        </li>
                        <li>
                          A deal is marked "Active" when a term-sheet is signed
                          and exits the pipeline when the deal is terminated.
                        </li>
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Badge className="min-w-0" variant={syndicationBadge.variant}>
                  {syndicationBadge.text}
                </Badge>
              </div>
              <div className="grid grid-cols-1 items-center gap-x-1 @[360px]:grid-cols-2">
                <div className="text-muted-foreground min-w-0 text-sm font-medium">
                  Deal Lead
                </div>
                <div className="flex min-w-0 items-center gap-x-2">
                  <TeamMemberSelector
                    initialSelectedIds={dealLead ? [dealLead.id] : []}
                    multiple={false}
                    onChange={ids => {
                      void (async () => {
                        try {
                          await updateDealOpportunity({
                            variables: {
                              deal_opportunity_id: id,
                              md_team_id: ids[0] ?? null
                            }
                          });
                        } catch (error) {
                          console.error('Failed to update Deal Lead:', error);
                          toast.error('Failed to update Deal Lead');
                        }
                      })();
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 items-center gap-x-1 @[360px]:grid-cols-2">
                <div className="text-muted-foreground min-w-0 text-sm font-medium">
                  Supporting Lead
                </div>
                <div className="flex min-w-0 items-center gap-x-2">
                  <TeamMemberSelector
                    initialSelectedIds={
                      supportingLead ? [supportingLead.id] : []
                    }
                    multiple={false}
                    onChange={ids => {
                      void (async () => {
                        try {
                          await updateDealOpportunity({
                            variables: {
                              deal_opportunity_id: id,
                              md2_team_id: ids[0] ?? null
                            }
                          });
                        } catch (error) {
                          console.error(
                            'Failed to update Supporting Lead:',
                            error
                          );
                          toast.error('Failed to update Supporting Lead');
                        }
                      })();
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 items-center gap-x-1 @[360px]:grid-cols-2">
                <div className="text-muted-foreground min-w-0 text-sm font-medium">
                  Mid-Level Team
                </div>
                <div className="flex min-w-0 items-center gap-x-2">
                  <TeamMemberSelector
                    initialSelectedIds={midLevelTeam.map(member => member.id)}
                    multiple={true}
                    onChange={ids => {
                      void (async () => {
                        try {
                          await updateDealOpportunity({
                            variables: {
                              deal_opportunity_id: id,
                              mid_team_ids: ids
                            }
                          });
                        } catch (error) {
                          console.error(
                            'Failed to update Mid-Level Team:',
                            error
                          );
                          toast.error('Failed to update Mid-Level Team');
                        }
                      })();
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 items-center gap-x-1 @[360px]:grid-cols-2">
                <div className="text-muted-foreground min-w-0 text-sm font-medium">
                  Junior Team
                </div>
                <div className="flex min-w-0 items-center gap-x-2">
                  <TeamMemberSelector
                    initialSelectedIds={juniorTeam.map(member => member.id)}
                    multiple={true}
                    onChange={ids => {
                      void (async () => {
                        try {
                          await updateDealOpportunity({
                            variables: {
                              deal_opportunity_id: id,
                              junior_team_ids: ids
                            }
                          });
                        } catch (error) {
                          console.error('Failed to update Junior Team:', error);
                          toast.error('Failed to update Junior Team');
                        }
                      })();
                    }}
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <Separator />
        <Tabs defaultValue="comments">
          <TabsList className="min-w-0 w-full">
            <TabsTrigger value="comments">Comments</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="files">Files</TabsTrigger>
          </TabsList>
          <TabsContent
            className="space-y-5"
            value="comments"
          >
            <CommentsTab id={id} />
          </TabsContent>
          <TabsContent value="activity">
            <ActivityTab id={id} />
          </TabsContent>
          <TabsContent value="files">
            {files.length === 0 ? (
              <div className="text-muted-foreground mt-3 mb-6 text-center text-sm">
                Get started by adding your first file.
              </div>
            ) : (
              <>
                <div className="space-y-2.5 pt-3">
                  {files.map(file => (
                    <div
                      className="flex items-center justify-between gap-x-1 border-b pb-2.5"
                      key={file.id}
                    >
                      <div className="flex min-w-0 items-center gap-x-1">
                        <div className="rounded-sm bg-teal-50 p-2 text-teal-900">
                          <FilesIcon />
                        </div>
                        <div className="min-w-0 space-y-1">
                          <TruncatedTextTooltip className="text-sm font-medium">
                            {file.name}
                          </TruncatedTextTooltip>
                          <div className="text-muted-foreground text-sm leading-none font-light">
                            {file.uploadDate}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-x-1">
                        {file.type === 'cim' &&
                          (hasCimMetadata ? (
                            <Popover
                              onOpenChange={setCimPopoverOpen}
                              open={cimPopoverOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  disabled={
                                    parseCimLoading || isPollingCimStatus
                                  }
                                  size="icon"
                                  variant="outline"
                                >
                                  {parseCimLoading || isPollingCimStatus ? (
                                    <Loader2Icon className="size-4 animate-spin" />
                                  ) : (
                                    <Wand2 className="size-4" />
                                  )}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="end"
                                className="w-48 p-2"
                              >
                                <div className="flex flex-col gap-1">
                                  <Button
                                    className="justify-start"
                                    onClick={handleViewParsedCimData}
                                    size="sm"
                                    variant="ghost"
                                  >
                                    View parsed data
                                  </Button>
                                  <Button
                                    className="justify-start"
                                    onClick={() => {
                                      setCimPopoverOpen(false);
                                      void handleTriggerParseCim();
                                    }}
                                    size="sm"
                                    variant="ghost"
                                  >
                                    Parse CIM
                                  </Button>
                                </div>
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <Button
                              disabled={parseCimLoading || isPollingCimStatus}
                              onClick={() => void handleTriggerParseCim()}
                              size="icon"
                              variant="outline"
                            >
                              {parseCimLoading || isPollingCimStatus ? (
                                <Loader2Icon className="size-4 animate-spin" />
                              ) : (
                                <Wand2 className="size-4" />
                              )}
                            </Button>
                          ))}
                        <Button
                          onClick={() => {
                            handleViewFile(file.fileData ?? '', file.name);
                          }}
                          size="icon"
                          variant="outline"
                        >
                          <EyeIcon />
                        </Button>
                        <Button
                          onClick={() => {
                            handleDownloadFile(file.fileData ?? '', file.name);
                          }}
                          size="icon"
                        >
                          <DownloadIcon />
                        </Button>
                        <Button
                          className="text-red-600"
                          onClick={() => void handleDeleteFile(file.type)}
                          size="icon"
                          variant="ghost"
                        >
                          <Trash2Icon />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Separator className="my-5" />
              </>
            )}
            <Button
              className="w-full"
              onClick={() => {
                setIsUploadSheetOpen(true);
              }}
            >
              <UploadIcon />
              Upload File
            </Button>
            <Sheet
              onOpenChange={open => {
                setIsUploadSheetOpen(open);
                if (!open) {
                  setSelectedFileType(undefined);
                  setSelectedFile(null);
                  setFileFieldErrorMessage(null);
                }
              }}
              open={isUploadSheetOpen}
            >
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Upload File</SheetTitle>
                </SheetHeader>
                <FieldGroup className="px-4">
                  <Field>
                    <FieldLabel htmlFor="file-type-field">File Type</FieldLabel>
                    <Select
                      onValueChange={setSelectedFileType}
                      value={selectedFileType}
                    >
                      <SelectTrigger id="file-type-field">
                        <SelectValue placeholder="Select file type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cim">CIM</SelectItem>
                        <SelectItem value="nda">NDA</SelectItem>
                        <SelectItem value="teaser">Teaser</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field data-invalid={fileFieldErrorMessage !== null}>
                    <FieldLabel htmlFor="file-field">File</FieldLabel>
                    <Input
                      accept=".pdf,application/pdf"
                      aria-invalid={fileFieldErrorMessage !== null}
                      id="file-field"
                      onChange={e => {
                        setSelectedFile(e.target.files?.[0] ?? null);
                        setFileFieldErrorMessage(null);
                      }}
                      type="file"
                    />
                    <FieldError>{fileFieldErrorMessage}</FieldError>
                  </Field>
                </FieldGroup>
                <SheetFooter>
                  <Button
                    disabled={!selectedFileType || !selectedFile}
                    onClick={() => {
                      void (async () => {
                        if (!selectedFile || !selectedFileType) return;
                        if (
                          selectedFile.type !== 'application/pdf' &&
                          !selectedFile.name.toLowerCase().endsWith('.pdf')
                        ) {
                          setFileFieldErrorMessage(
                            'Only PDF files are allowed'
                          );
                          return;
                        }
                        setFileFieldErrorMessage(null);

                        try {
                          const base64 = await fileToBase64(selectedFile);

                          if (selectedFileType === 'cim') {
                            await uploadCim({
                              variables: {
                                cim: base64,
                                id
                              }
                            });
                          } else if (selectedFileType === 'nda') {
                            await uploadNda({
                              variables: {
                                id,
                                nda: base64
                              }
                            });
                          } else if (selectedFileType === 'teaser') {
                            await uploadTeaser({
                              variables: {
                                id,
                                teaser: base64
                              }
                            });
                          }

                          toast.success('File uploaded successfully');
                          setIsUploadSheetOpen(false);
                          setSelectedFileType(undefined);
                          setSelectedFile(null);
                          setFileFieldErrorMessage(null);
                        } catch (error) {
                          console.error('Error uploading file:', error);
                          toast.error('Failed to upload file');
                        }
                      })();
                    }}
                  >
                    <UploadIcon />
                    Upload File
                  </Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </TabsContent>
        </Tabs>
      </div>
      <PdfViewerDialog
        description="View file content"
        filename={viewerFilename}
        onOpenChange={open => {
          setIsViewerOpen(open);
          if (!open) {
            setViewerFileData(null);
            setViewerFilename('');
          }
        }}
        open={isViewerOpen}
        pdfData={viewerFileData}
        title="File Viewer"
      />
      <ParseCimModal
        data={parsedCimData}
        onApply={
          id
            ? async cimData => {
                try {
                  const result = await updateOpportunityFromCimData({
                    variables: {
                      data: cimData,
                      opportunityId: id
                    }
                  });
                  const payload = result.data?.updateOpportunityFromCimData;
                  if (payload?.ok) {
                    toast.success('Opportunity updated from CIM data');
                    setParseCimModalOpen(false);
                  } else {
                    toast.error(
                      payload?.message ?? 'Failed to update opportunity'
                    );
                  }
                } catch (error) {
                  console.error(
                    'Error updating opportunity from CIM data:',
                    error
                  );
                  toast.error('Failed to update opportunity');
                }
              }
            : undefined
        }
        onDataChange={setParsedCimData}
        onOpenChange={setParseCimModalOpen}
        open={parseCimModalOpen}
      />
    </>
  );
};

export default RightSidebar;
